import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { Action } from "shared/types/Action"
import { ActionButtons } from "shared/ui/action-buttons/ActionButtons"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Popover } from "shared/ui/popover/Popover"

import "../styles/_feedback.scss"

export interface FeedbackButtonViewModel {
  setTarget: (element: HTMLElement | null) => void
  feedbackActions: Action[]
  isHidden?: boolean
  isMenuVisible: boolean
  onClick: React.MouseEventHandler
  onOpenChange: (open: boolean) => void
}

export const FeedbackButtonView = React.memo(
  ({
    setTarget,
    feedbackActions,
    isMenuVisible,
    isHidden,
    onOpenChange,
    onClick
  }: FeedbackButtonViewModel) => {
    const { t } = useTranslation()

    if (isHidden) {
      return null
    }

    return (
      <>
        <Button
          ref={setTarget}
          id="feedback-button"
          data-testid="feedback-button--mobile"
          variant={ButtonVariant.PRIMARY}
          className={classNames("feedback-button feedback-button--mobile")}
          onClick={onClick}
          isHidden={isHidden}
        >
          <Icon name={IconName.THUMB_UP} />
          <span className="feedback-button__label">
            {t("Feedback.Feedback")}
          </span>
        </Button>
        <Popover.Root open={isMenuVisible} onOpenChange={onOpenChange}>
          <Popover.Trigger
            ref={setTarget}
            id="feedback-button"
            data-testid="feedback-button--desktop"
            className={classNames(
              "btn btn-primary feedback-button feedback-button--desktop",
              {
                "visually-hidden": isMenuVisible
              }
            )}
          >
            <Icon name={IconName.THUMB_UP} />
            <span className="feedback-button__label">
              {t("Feedback.Feedback")}
            </span>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Positioner sideOffset={8}>
              <Popover.Popup
                className={classNames("feedback-button__popup")}
                // Base UI renders the popup with role="dialog", which requires
                // an accessible name.
                aria-label={t("Feedback.Feedback")}
              >
                <ActionButtons
                  actions={feedbackActions}
                  label={t("Feedback.Feedback")}
                />
              </Popover.Popup>
            </Popover.Positioner>
          </Popover.Portal>
        </Popover.Root>
      </>
    )
  }
)
